import React, { useEffect, useState } from "react";
import NewArrivalProductCard from "./NewArrivalProductCard";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function NewArrivalPage() {
  const [newArrivals, setNewArrivals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL

  // Fetch new arrivals from the API
  useEffect(() => {
    const fetchNewArrivals = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_BASE_URL}/new-arrival`);
        
        // Parse the response regardless of status code
        const data = await response.json();
        
        // Check if the response is negative or indicates no new arrivals
        if (!response.ok || !data.new_arrivals || data.new_arrivals.length === 0) {
          setNewArrivals([]);
          setError("no_arrivals"); // Special error state for no arrivals
        } else {
          setNewArrivals(data.new_arrivals);
          setError(null);
        }
      } catch (error) {
        console.error("Error fetching new arrivals:", error);
        // Only show generic error for actual network/parsing errors
        setError("Failed to fetch new arrivals");
      } finally {
        setIsLoading(false);
      }
    };

    fetchNewArrivals();
  }, []);

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
      {[...Array(8)].map((_, index) => (
        <div 
          key={index} 
          className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse"
        >
          <div className="aspect-square bg-gray-200"></div>
          <div className="p-4 space-y-3">
            <div className="h-5 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="h-10 bg-gray-200 rounded mt-4"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // No arrivals component
  const NoArrivals = () => (
    <div className="text-center py-12">
      <div className="text-gray-500 mb-4 text-lg">
        No new arrivals available at the moment
      </div>
      <p className="text-gray-400 mb-6">
        Check back soon for our latest fragrance collection
      </p>
      <button 
        onClick={() => window.location.reload()}
        className="bg-[#D4AF37] text-black px-6 py-2 rounded-sm font-medium hover:bg-[#C19B2E] transition-all"
      >
        Refresh
      </button>
    </div>
  );

  // Pagination logic
  const totalPages = Math.ceil(newArrivals.length / productsPerPage);
  const currentProducts = newArrivals.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with animation */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <h1
            className="text-4xl md:text-5xl text-[#333333] font-serif font-medium mb-4"
          >
            New Arrivals
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            Discover our latest fragrance collection - fresh, modern scents crafted for the discerning individual.
          </p>
          <div className="w-32 h-1 bg-[#D4AF37] mx-auto rounded-full"></div>
        </motion.div>

        {/* Content */}
        {isLoading ? (
          <LoadingSkeleton />
        ) : error === "no_arrivals" ? (
          <NoArrivals />
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 mb-4">{error}</div>
            <button 
              onClick={() => window.location.reload()}
              className="bg-[#D4AF37] text-black px-6 py-2 rounded-sm font-medium hover:bg-[#C19B2E] transition-all"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Product Grid with staggered animation */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.1,
                  },
                },
              }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {currentProducts.map((product) => (
                <motion.div
                  key={product.id}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.3 }}
                  className="flex justify-center"
                >
                  <NewArrivalProductCard product={product} />
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-12">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        currentPage === page 
                          ? 'bg-[#D4AF37] text-black' 
                          : 'hover:bg-gray-100'
                      } transition-colors`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}